import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from "@/components/ui/button";
import { PaymentMethodEnum } from "@/enum";
import { DiscountType, formDataOrderMapper, getInitialValueForEditOrder, orderApi, useEditOrderMutation} from "@/features/order";
import { useGetCustomerListQuery } from "@/features/customer";
import CustomerModal from "@/features/customer/components";
import {
  Product,
  SellerBuyerRootMapping,
  SellerProductDetail,
} from "@/interfaces";
import { debounce } from "lodash";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";
import { toast } from "react-toastify";
import { useGetSellerProductListQuery } from "@/features/seller";
import { LIMIT } from "@/constants";
import { useLazyGetProductByBarcodeQuery } from "@/features/product";
import { useBarcodeScanner } from "@/hooks/useBarcode";
import { currencyFormatter, decimal, DecimalCalc } from "@/utils";
import { useGetPaymentMethodQuery } from "@/features/payment";
import {
  IndianRupee,
  Percent,
  User2
} from "lucide-react";
import '../../create/Create.css';
import { appColors } from "@/constants/color";
import { store } from '@/store';
import { formatDateToIST, formatTimeToIST } from '@/utils/common';
import { useSelector } from 'react-redux';
import { currentUserSelector } from '@/features/auth';

const toggleIcon = "/icons/Toggle.png";
const userIcon = "/icons/user.png";
export const Route = createFileRoute('/(app)/(store)/sales/$orderId/edit/')({
  loader: async ({ params }) => {
    const result = await store.dispatch(
      orderApi.endpoints.getOrder.initiate({
        orderId: params.orderId, "seller-store-detail": "true",
        "buyer-store-detail": "true",
        'buyer-detail': 'true',
        'seller-product-detail': "true"
      }, { forceRefetch: true })
    );
    if ("error" in result) throw result.error;
    return result.data
  },
  component: RouteComponent,
})


function RouteComponent() {
  const { data: accounts = [] } = useGetPaymentMethodQuery({});
  const order = Route.useLoaderData()
  const methods = useForm({
    defaultValues: {
      ...getInitialValueForEditOrder(order),
    },
  });
  const [editOrder, { isLoading }] = useEditOrderMutation();
  const navigate = useNavigate();

  const [isPaymentSummaryExpanded, setIsPaymentSummaryExpanded] =
    useState(true);
  const [isPaymentMethodExpanded, setIsPaymentMethodExpanded] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [orderData, setOrderData] = useState(null);

  const isApplied = methods.watch("isApplied");
  const discount = methods.watch("discount");
  const watchedItems = methods.watch("productList") || [];
  const allNoProductDiscount = watchedItems.every((item: any) =>
    !item?.price?.mrp || item?.price?.mrp <= item?.price?.sellingPrice
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const togglePaymentSummary = () => {
    setIsPaymentSummaryExpanded((prev) => !prev);
    setIsPaymentMethodExpanded(!isPaymentMethodExpanded);
  };

  const togglePaymentMethod = () => {
    setIsPaymentMethodExpanded((prev) => !prev);
    setIsPaymentSummaryExpanded(!isPaymentSummaryExpanded);
  };
  const onSubmit = async (data: any) => {
    try {
      const payload = formDataOrderMapper(data);
      const response = await editOrder({ orderId: order?._id, ...payload }).unwrap();
      if (response.data) {
        toast.success("Order updated Successfully.");
        methods.reset(getInitialValueForEditOrder(order));
        setOrderData(response?.data);
        setShowPrintModal(true);
        window.localStorage.setItem(
          "create-invoice-paymentMethod",
          data?.paymentMethod
        );
      }
    } catch (error: any) {
      toast.error(error?.data.message ?? "Something went wrong.");
    }
  };

  const handlePrintConfirm = () => {
    setShowPrintModal(false);
    navigate({
      to: "/order/$orderId",
      params: { orderId: orderData?._id },
      search: { print: true }, // Add print parameter
    });
  };

  const handlePrintSkip = () => {
    setShowPrintModal(false);
    navigate({
      to: "/order/$orderId",
      params: { orderId: orderData?._id },
      search: { print: false }, // Explicitly set print to false
    });
  };

  // Print Confirmation Modal Component
  const PrintConfirmModal = () => (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: appColors.modalOverlay,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="modal-content" style={{
        backgroundColor: appColors.surfaceBase,
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '400px',
        width: '90%',
        textAlign: 'center',
        boxShadow: `0 10px 25px ${appColors.shadowOverlay}`
      }}>
        <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>
          Bill Generated Successfully!
        </h3>
        <p style={{ marginBottom: '24px', color: appColors.modalText, fontSize: '14px' }}>
          Would you like to print the bill now?
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <Button
            variant="outline"
            onClick={handlePrintSkip}
            style={{ minWidth: '100px' }}
          >
            Skip
          </Button>
          <Button
            onClick={handlePrintConfirm}
            style={{ minWidth: '100px' }}
          >
            Print Bill
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <FormProvider {...methods}>
        <form
          className="generateBill"
          onSubmit={methods.handleSubmit(onSubmit)}
          onKeyDown={handleKeyDown}
        >
          <div className="billLeft">
            <div className="flex justify-between items-center">
              <h1 className="f500 fs24">Order No: {order?.invoiceNumber}</h1>
              <span>
                {formatDateToIST(order?.updatedAt)} |{" "}
                {formatTimeToIST(order?.updatedAt).toUpperCase()}
              </span>
            </div>
            <CustomerSelector />
            <InvoiceItems />
            {isApplied && discount?.value > 0 && allNoProductDiscount && (
              <div className="discountBanner" style={{
                border: `1px solid ${appColors.discountBannerBorder}`,
                backgroundColor: appColors.discountBadgeBg,
                color: appColors.discountBadgeText,
              }}>
                🎉 {discount.type === DiscountType.Percent
                  ? `${discount.value}%`
                  : `₹${discount.value}`} discount applied to all items
              </div>
            )}
          </div>
          <div className="billRight">
            <PaymentSummary
              isExpanded={isPaymentSummaryExpanded}
              toggleExpanded={togglePaymentSummary}
            />
            <PaymentMethod
              isExpanded={isPaymentMethodExpanded}
              toggleExpanded={togglePaymentMethod}
              accounts={accounts}
            />
          </div>
          <div className="buttonRow generateBtn">
            <Button type="submit" size="xl" disabled={isLoading}>
              Update Bill
            </Button>
          </div>
        </form>
      </FormProvider>

      {/* Render Print Confirmation Modal */}
      {showPrintModal && <PrintConfirmModal />}
    </>
  );
}

function CustomerSelector() {
  const { setValue, watch } = useFormContext();
  const order = Route.useLoaderData();
  const selectedCustomer = watch("customerDetails");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [openCustomerModal, setOpenCustomerModal] = useState<boolean>(false);
  const [query, setQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { data: customers = [] } = useGetCustomerListQuery({
    buyer: true,
    page: 1,
    limit: 10,
    search: query,
  });

  // Pre-populate the input with the existing customer name
  const initialCustomerName = order.buyerStore?.name || order.buyerUser?.name || "";

  useEffect(() => {
    if (searchInputRef.current && !selectedCustomer && initialCustomerName) {
      searchInputRef.current.value = initialCustomerName;
    }
  }, [initialCustomerName]);

  // Handle clicks outside the dropdown
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setShowSuggestions(false);
    }
  }, []);

  useMemo(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  const handleCustomerSelect = (customer: SellerBuyerRootMapping) => {
    setValue("customerDetails", customer);
    if (searchInputRef.current) {
      searchInputRef.current.value =
        (customer.buyerName as string) ||
        (customer?.buyerPhoneNumber as string);
    }
    setShowSuggestions(false);
  };
  const debouncedSetQuery = useCallback(
    debounce((val: string) => setQuery(val), 300),
    []
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      debouncedSetQuery(event.target.value);
      if (!showSuggestions) {
        setShowSuggestions(true);
      }
    },
    [showSuggestions, debouncedSetQuery]
  );
  const renderCustomer = ({
    customer,
  }: {
    customer: SellerBuyerRootMapping;
  }) => {
    return (
      <div
        className="dropdownItem"
        onClick={() => handleCustomerSelect(customer)}
      >
        <div className="customerInfo">
          <strong>{customer.buyerName}</strong>
          <span>{customer?.buyerPhoneNumber}</span>
        </div>
      </div>
    );
  };
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSuggestions]);

  return (
    <div className="customerCard">
      <div className="itemsHeader">
        <h3 className="containerHeading">Customer</h3>
        <button
          type="button"
          className="addItemsButton"
          onClick={() => setOpenCustomerModal(true)}
        >
          + Add New Customer
        </button>
        <CustomerModal
          isOpen={openCustomerModal}
          onClose={() => setOpenCustomerModal(false)}
          clientType="Buyer"
          onCustomerCreated={handleCustomerSelect}
        />
      </div>
      <div className="customerInputContainer">
        <div className="product-dropdown" style={{ width: "100%" }}>
          <input
            ref={searchInputRef}
            className="customerInput"
            type="text"
            placeholder="Enter or Search for customer"
            onChange={handleChange}
          />
          <img src={userIcon} alt="User" className="customerInputIcon" />

          {showSuggestions && (
            <div className="dropdownCard" ref={dropdownRef}>
              {customers?.length > 0 ? (
                customers?.map((customer) =>
                  renderCustomer({ customer: customer })
                )
              ) : (
                <div className="dropdown-empty">No customer found</div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="customerTags">
        {customers.slice(0, 6).map((customer) => (
          <span
            key={customer._id}
            className={`customerTag ${selectedCustomer === customer.buyerName ? "customerTagSelected" : ""}`}
            onClick={() => handleCustomerSelect(customer)}
          >
            {customer.buyerName || customer.buyerPhoneNumber}
          </span>
        ))}
      </div>
    </div>
  );
}
function InvoiceItems() {
  const {
    watch,
    setValue,
    register,
    control,
    formState: { errors },
  } = useFormContext();
  const [query, setQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [page] = useState(1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentUser = useSelector(currentUserSelector);
  const [showPurchasePrice, setShowPurchasePrice] = useState(false);
  const canViewPurchasePrice = currentUser?.profileDetail?.saleOrderConfig?.showPurchasePrice ?? true;
  const {
    data: productList,
    isLoading,
    error,
  } = useGetSellerProductListQuery({
    search: query,
    limit: LIMIT * 2,
    page,
  });
  const items = watch("productList") || [];
  const isApplied = watch("isApplied") || false;
  const discount = watch("discount") || { type: DiscountType.Percent, value: 0 };

  const itemTotal = items.reduce((sum: number, item: any) =>
    sum + (item?.price?.sellingPrice || 0) * (item?.quantity || 0), 0);

  const allocatedDiscounts = useMemo(() => {
    if (!isApplied || !discount?.value || discount.value <= 0 || itemTotal <= 0) {
      return items.map(() => 0);
    }

    // 1. Calculate the total header discount in Rupees first
    let totalDiscountInRs = 0;
    if (discount.type === DiscountType.Percent) {
      totalDiscountInRs = DecimalCalc.percentage(itemTotal, discount.value).toDecimalPlaces(2).toNumber();
    } else {
      totalDiscountInRs = DecimalCalc.min([discount.value, itemTotal]).toNumber();
    }

    // 2. Run the allocation algorithm to get an array of rupee discounts
    return DecimalCalc.allocateDiscount(items, totalDiscountInRs);
  }, [isApplied, discount?.value, discount?.type, itemTotal, items]);

  // Handle clicks outside the dropdown
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setShowSuggestions(false);
    }
  }, []);

  // Set up event listener for
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);
  const [getProductByBarcode] = useLazyGetProductByBarcodeQuery();

  useBarcodeScanner(async (barcode) => {
    const response = await getProductByBarcode({
      barcode: barcode.trim(),
    }).unwrap();
    if (response.data) {
      handleSelect({
        name: response.data.name,
        taxPercentageList: response.data.taxPercentageList,
        imageList: response.data.imageList,
        ...response.data?.sellerProductDetail?.at(0),
        quantity:
          response.data?.sellerProductDetail?.at(0)?.minimumQuantity
            ?.quantity ?? 1,
      });
    }
  });

  const updateQty = (index: number, delta: number) => {
    const updated = [...items];
    const currentQty = parseInt(updated[index].quantity) || 1;
    const initialQty = parseInt(updated[index].initialQuantity || 1);

    // Always use step of 1 for whole numbers only
    const step = 1;

    // Calculate the new quantity
    const newQty = currentQty + (delta * step);

    if (newQty < initialQty) {
      updated.splice(index, 1);
    } else {
      // Keep as integer
      updated[index].quantity = Math.max(initialQty, Math.floor(newQty));
    }
    setValue("productList", updated);
  };

  const removeItem = (index: number) => {
    const updated = [...items];
    updated.splice(index, 1);
    setValue("productList", updated);
  };

  const handlePriceChange = (
    index: number,
    field: "mrp" | "sellingPrice",
    value: number
  ) => {
    const updated = [...items];
    updated[index]["price"][field] = value;
    setValue("productList", updated);
  };

  const handleSelect = (product: any) => {
    const existingItemIndex = items.findIndex(
      (item: any) => item._id === product._id
    );
    const initialQuantity = product.quantity;
    if (existingItemIndex > -1) {
      const updatedItems = [...items];
      // Always increment by 1 (whole numbers only)
      updatedItems[existingItemIndex].quantity = 
        parseInt(updatedItems[existingItemIndex].quantity) + 1;
      setValue("productList", updatedItems);
    } else {
      setValue("productList", [...items, { ...product, initialQuantity }]);
    }
    setShowSuggestions(false);
    setQuery("");
  };
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  }, []);

  const debouncedSetQuery = useCallback(
    debounce((val: string) => setQuery(val), 300),
    []
  );
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      debouncedSetQuery(event.target.value);
      if (!showSuggestions) {
        setShowSuggestions(true);
      }
    },
    [showSuggestions, debouncedSetQuery]
  );

  const handleFocus = useCallback(() => {
    setQuery("");
    if (!showSuggestions) {
      setShowSuggestions(true);
    }
  }, [showSuggestions]);

  const handleQuantityChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, index: number, cb: Function) => {
      const initialQty = parseInt(items[index].minimumQuantity?.quantity || 1);
      const newValue = parseInt(event.target.value);

      if (isNaN(newValue) || newValue < initialQty) {
        cb(initialQty);
      } else {
        // Keep as integer
        cb(Math.floor(newValue));
      }
    },
    []
  );

  const renderItem = ({ item }: { item: any }) => {
    if (item?.sellerProductDetail?.length > 1) {
      return item.sellerProductDetail.map(
        (sellerProductDetail: SellerProductDetail) =>
          renderItem({
            item: {
              ...item,
              sellerProductDetail: [{ ...sellerProductDetail }],
            },
          } as any)
      );
    }
    return (
      <div
        className="dropdownItem"
        onClick={() =>
          handleSelect({
            name: item.name,
            taxPercentageList: item.taxPercentageList,
            imageList: item.imageList,
            ...item?.sellerProductDetail?.at(0),
            quantity:
              item?.sellerProductDetail?.at(0)?.minimumQuantity?.quantity ?? 1,
          } as any)
        }
      >
        <img
          src={item.imageList?.at(0)}
          alt={item.name}
          className="dropDownImage"
        />
        <div className="productDropDownContainer">
          <strong>{item.name}</strong>
          <div className="productDetails">
            {item?.sellerProductDetail?.at(0)?.price?.mrp?.toFixed(2) && (
              <span>
                MRP: ₹{item?.sellerProductDetail?.at(0)?.price?.mrp?.toFixed(2)}
              </span>
            )}

            {item?.sellerProductDetail
              ?.at(0)
              ?.price?.sellingPrice?.toFixed(2) && (
                <span>
                  Selling: ₹
                  {item?.sellerProductDetail
                    ?.at(0)
                    ?.price?.sellingPrice?.toFixed(2)}
                </span>
              )}

            {item?.sellerProductDetail?.at(0)?.barcode && (
              <span>barcode: {item?.sellerProductDetail?.at(0)?.barcode}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleToggle = useCallback(() => {
    setShowPurchasePrice((pre) => !pre);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false); // <-- trigger close
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSuggestions]);
  return (
    <div className="itemsCard">
      <div className="itemsHeader">
        <h3 className="containerHeading">Invoice Items</h3>
        <Button
          type="button"
          variant="link"
          to="/product/add-product"
          className="addItemsButton"
        >
          + Add Items
        </Button>
      </div>
      <div className="productDropdown" ref={dropdownRef} style={{ display: 'block' }}>
        <input
          type="text"
          placeholder="Search products..."
          style={{ width: canViewPurchasePrice ? 'calc(100% - 40px)' : '100%' }}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
        />
        {canViewPurchasePrice && (
          <button
            className="purchasePrice"
            onClick={() => setShowPurchasePrice(!showPurchasePrice)}
            type="button"
            style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <p style={{ margin: 0, color: appColors.accentBlue, fontSize: '14px', fontWeight: 500 }}>Purchase Price</p>
            {showPurchasePrice ? (
              <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 20 20" fill="none">
                <path d="M9.99967 7.5C10.6627 7.5 11.2986 7.76339 11.7674 8.23223C12.2363 8.70107 12.4997 9.33696 12.4997 10C12.4997 10.663 12.2363 11.2989 11.7674 11.7678C11.2986 12.2366 10.6627 12.5 9.99967 12.5C9.33663 12.5 8.70075 12.2366 8.23191 11.7678C7.76307 11.2989 7.49967 10.663 7.49967 10C7.49967 9.33696 7.76307 8.70107 8.23191 8.23223C8.70075 7.76339 9.33663 7.5 9.99967 7.5ZM9.99967 3.75C14.1663 3.75 17.7247 6.34167 19.1663 10C17.7247 13.6583 14.1663 16.25 9.99967 16.25C5.83301 16.25 2.27467 13.6583 0.833008 10C2.27467 6.34167 5.83301 3.75 9.99967 3.75ZM2.64967 10C3.32322 11.3753 4.3691 12.534 5.66841 13.3444C6.96772 14.1548 8.46834 14.5844 9.99967 14.5844C11.531 14.5844 13.0316 14.1548 14.3309 13.3444C15.6303 12.534 16.6761 11.3753 17.3497 10C16.6761 8.62474 15.6303 7.46604 14.3309 6.65562C13.0316 5.8452 11.531 5.41557 9.99967 5.41557C8.46834 5.41557 6.96772 5.8452 5.66841 6.65562C4.3691 7.46604 3.32322 8.62474 2.64967 10Z" fill={appColors.accentBlue} />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 20 20" fill="none">
                <path d="M1.66634 4.39159L2.73301 3.33325L16.6663 17.2666L15.608 18.3333L13.0413 15.7666C12.083 16.0833 11.0663 16.2499 9.99967 16.2499C5.83301 16.2499 2.27467 13.6583 0.833008 9.99992C1.40801 8.53325 2.32467 7.24159 3.49134 6.21659L1.66634 4.39159ZM9.99967 7.49992C10.6627 7.49992 11.2986 7.76331 11.7674 8.23215C12.2363 8.70099 12.4997 9.33688 12.4997 9.99992C12.5001 10.2837 12.4522 10.5655 12.358 10.8333L9.16634 7.64159C9.43406 7.5474 9.71587 7.4995 9.99967 7.49992ZM9.99967 3.74992C14.1663 3.74992 17.7247 6.34159 19.1663 9.99992C18.4858 11.7274 17.3301 13.2268 15.833 14.3249L14.6497 13.1333C15.8021 12.3361 16.7316 11.2575 17.3497 9.99992C16.6761 8.62481 15.6301 7.46628 14.3308 6.65605C13.0315 5.84581 11.5309 5.41639 9.99967 5.41659C9.09134 5.41659 8.19968 5.56659 7.36634 5.83325L6.08301 4.55825C7.28301 4.04159 8.60801 3.74992 9.99967 3.74992ZM2.64967 9.99992C3.3233 11.375 4.36922 12.5336 5.66854 13.3438C6.96785 14.154 8.46843 14.5834 9.99967 14.5833C10.5747 14.5833 11.1413 14.5249 11.6663 14.4083L9.76634 12.4999C9.18648 12.4378 8.64536 12.179 8.23299 11.7666C7.82061 11.3542 7.56183 10.8131 7.49967 10.2333L4.66634 7.39158C3.84134 8.09992 3.14967 8.98325 2.64967 9.99992Z" fill={appColors.accentBlue} />
              </svg>
            )}
          </button>
        )}

        {showSuggestions && (
          <div className="dropdownCard">
            {isLoading ? (
              <div className="dropdownLoading">Searching products...</div>
            ) : error ? (
              <div className="dropdownError">Failed to load products</div>
            ) : productList && productList.length > 0 ? (
              productList.map((product) => renderItem({ item: product }))
            ) : query ? (
              <div className="dropdownEmpty">No products found</div>
            ) : (
              <div className="dropdownEmpty">
                Start typing to search products
              </div>
            )}
          </div>
        )}
      </div>
      {items.map(
        (
          item: Product &
            SellerProductDetail & { quantity: number; initialQuantity: number },
          i: number
        ) => {
          const isAtInitialQty =
            parseFloat(item?.quantity) === parseFloat(item.minimumQuantity?.quantity || 1);

          const allocatedDiscountPrice = allocatedDiscounts[i] || 0;

          // 1. Total price at MRP (pre-discount)
          const itemOriginalPrice = item.quantity * item.price.mrp;
          // 2. Total final price (qty * sellingPrice - this item's share of the header discount)
          const itemFinalPrice = Math.max(0, (item.quantity * item.price.sellingPrice) - allocatedDiscountPrice);
          // 3. Total discount amount (MRP discount + header discount share)
          const totalItemDiscount = Math.max(0, itemOriginalPrice - itemFinalPrice);
          // 4. Determine if we show a discount display
          const showDiscount = totalItemDiscount > 0 && item.price.mrp > 0;
          // 5. Calculate the effective unit price after all discounts (for display)
          const effectivePrice = item.quantity > 0 ? itemFinalPrice / item.quantity : item.price.sellingPrice;
          // 6. Combined discount percentage for the badge
          const discountPercent = showDiscount ? Math.round((totalItemDiscount / itemOriginalPrice) * 100) : 0;

          return (
            <div className="invoiceItem" key={item._id}>
              <div className="itemDetails">
                <div className="itemImageContainer">
                  <img
                    src={
                      item?.imageList?.at(0) || "https://via.placeholder.com/50"
                    }
                    alt="item"
                    className="itemImage"
                  />
                </div>
                <div className="itemInfo">
                  <p className="itemName">{item.name}</p>
                  <section>
                    <p className="itemPrice">
                      {item?.quantity}X{" "}
                      {showDiscount && (
                        <span style={{ textDecoration: "line-through", color: appColors.strikethrough }}>
                          {currencyFormatter(item?.price?.mrp)}
                        </span>
                      )}{" "}
                      {currencyFormatter(showDiscount ? effectivePrice : item?.price?.sellingPrice)}
                    </p>
                    {showDiscount && (
                      <div style={{
                        width: "50px",
                        height: "19px",
                        backgroundColor: appColors.discountBadgeBg,
                        borderRadius: "4px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: "2px",
                      }}>
                        <span style={{
                          color: appColors.discountBadgeText,
                          fontFamily: "Inter",
                          fontWeight: 500,
                          fontSize: "8px",
                        }}>
                          {discountPercent}% OFF
                        </span>
                      </div>
                    )}
                    {showPurchasePrice && (
                      <p className="itemPrice">
                        Purchase Price{" "}
                        {currencyFormatter(item.price.purchasePrice)}
                      </p>
                    )}
                  </section>
                </div>
              </div>

              <div className="itemControls">
                <div className="quantityControls">
                  {isAtInitialQty ? (
                    <button
                      type="button"
                      className="quantityButton"
                      style={{ color: appColors.dangerRed }}
                      onClick={() => removeItem(i)}
                      title="Remove item"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="dustbinIcon"
                      >
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="quantityButton"
                      onClick={() => updateQty(i, -1)}
                    >
                      -
                    </button>
                  )}
                  <Controller
                    control={control}
                    name={`productList.${i}.quantity`}
                    render={({
                      field: { onBlur, onChange, value },
                      fieldState: { error },
                    }) => (
                      <input
                        type="number"
                        step="1"
                        className={`quantityInput ${error ? "quantityInputError" : ""}`}
                        value={value}
                        onBlur={onBlur}
                        onChange={(event) =>
                          handleQuantityChange(event, i, onChange)
                        }
                        onWheel={(e) => e.currentTarget.blur()}
                      />
                    )}
                  />
                  <button
                    type="button"
                    className="quantityButton"
                    onClick={() => updateQty(i, 1)}
                  >
                    +
                  </button>
                </div>
                <div className="priceDisplay">
                  <p className="priceLabel">MRP</p>
                  <input
                    type="number"
                    className="priceInput"
                    defaultValue={item?.price?.mrp}
                    value={item?.price?.mrp}
                    onChange={(e) =>
                      handlePriceChange(i, "mrp", Number(e.target.value))
                    }
                    min="0"
                    onWheel={(e) => e.currentTarget.blur()}
                    disabled
                  />
                </div>
                <div className="validationContainer">
                  <div className="priceDisplay">
                    <p className="priceLabel">Selling</p>
                    <input
                      {...register(`productList.${i}.price.sellingPrice`, {
                        validate: (value) =>
                          Number(value) <= Number(item?.price?.mrp) ||
                          "Selling Price must be less then MRP",
                      })}
                      min="0"
                      step={"0.1"}
                      type="number"
                      className="priceInput"
                      defaultValue={item?.price?.mrp}
                      value={allocatedDiscountPrice > 0
                        ? Math.round(effectivePrice * 100) / 100
                        : item?.price.sellingPrice}
                      readOnly={allocatedDiscountPrice > 0}
                      onWheel={(e) => e.currentTarget.blur()}
                    />
                  </div>
                  {errors.productList?.[i]?.price?.sellingPrice && (
                    <p className="errorMessage">
                      {errors.productList[i].price.sellingPrice.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        }
      )}
    </div>
  );
}

const PaymentMethod = ({ isExpanded, toggleExpanded, accounts }) => {
  const { register, watch, setValue } = useFormContext();
  const order = Route.useLoaderData()
  const [isSplitActive, setIsSplitActive] = useState(order.payment.paymentMethodList.length > 1);
  const [defaultMethodSet, setDefaultMethodSet] = useState(false);
  const selectedPaymentMethods = watch("paymentMethods") || [];
  const selectedMethod = watch("paymentMethod");
  const splitAmounts = watch("splitAmounts") || {};

  useEffect(() => {
    if (accounts.length > 0 && !defaultMethodSet) {
      const savedMethod = localStorage.getItem("create-invoice-paymentMethod");

      if (savedMethod) {
        // Check if saved method is a valid account ID or enum value
        const isValidAccount = accounts.some((acc) => acc._id === savedMethod);
        const isValidEnum = Object.values(PaymentMethodEnum).includes(
          savedMethod as PaymentMethodEnum
        );

        if (isValidAccount || isValidEnum) {
          setValue("paymentMethod", savedMethod);
          setValue("paymentMethods", [savedMethod]);
        } else {
          // Fallback to first UPI or cash
          const firstUpiAccount = accounts.find((acc) => acc.upi?.upiId);
          const defaultMethod = firstUpiAccount
            ? firstUpiAccount._id
            : PaymentMethodEnum.Cash;
          setValue("paymentMethod", defaultMethod);
          setValue("paymentMethods", [defaultMethod]);
          localStorage.setItem("create-invoice-paymentMethod", defaultMethod);
        }
      } else {
        // Set default if no saved method
        const firstUpiAccount = accounts.find((acc) => acc.upi?.upiId);
        const defaultMethod = firstUpiAccount
          ? firstUpiAccount._id
          : PaymentMethodEnum.Cash;
        setValue("paymentMethod", defaultMethod);
        setValue("paymentMethods", [defaultMethod]);
        localStorage.setItem("create-invoice-paymentMethod", defaultMethod);
      }

      setDefaultMethodSet(true);
    }
  }, [accounts, defaultMethodSet, setValue]);

  useEffect(() => {
    if (isSplitActive) {
      const currentMethods = selectedPaymentMethods;
      const updatedMethods = [];

      // Check all payment methods
      const allMethods = [
        ...accounts.map(acc => acc._id),
        ...Object.values(PaymentMethodEnum)
      ];

      allMethods.forEach(methodId => {
        const amount = parseFloat(splitAmounts[methodId] || 0);

        if (amount > 0) {
          updatedMethods.push(methodId);
        }
      });

      // Update payment methods if changed
      if (JSON.stringify(updatedMethods.sort()) !== JSON.stringify(currentMethods.sort())) {
        setValue("paymentMethods", updatedMethods);
      }
    }
  }, [splitAmounts, isSplitActive, setValue]);

  const activateSplitPayment = () => {
    setIsSplitActive(true);
    // Initialize split amounts when activating split payment
    const initialSplitAmounts = {};
    accounts.forEach((account) => {
      initialSplitAmounts[account._id] = "";
    });
    Object.values(PaymentMethodEnum).forEach((method) => {
      initialSplitAmounts[method] = "";
    });
    setValue("splitAmounts", initialSplitAmounts);
    setValue("paymentMethods", []);
  };

  const deactivateSplitPayment = () => {
    setIsSplitActive(false);
    // Clear split amounts when deactivating split payment
    setValue("splitAmounts", {});
    // Reset to single selection mode
    if (selectedPaymentMethods.length > 0) {
      setValue("paymentMethod", selectedPaymentMethods[0]);
    }
  };

  const handleMethodSelection = (methodId) => {
    if (!isSplitActive) {
      const methodToStore = accounts.find((acc) => acc._id === methodId)
        ? methodId // Store account ID for UPI
        : methodId; //
      // Single selection mode - update both paymentMethod and paymentMethods
      setValue("paymentMethod", methodId);
      setValue("paymentMethods", [methodId]);
      localStorage.setItem("create-invoice-paymentMethod", methodToStore);
    }
  };

  const getDisplayMethod = () => {
    if (isSplitActive) {
      if (selectedPaymentMethods.length === 0) return "";

      const methodNames = selectedPaymentMethods.map((methodId) => {
        // Check if it's a UPI account
        const account = accounts.find((acc) => acc._id === methodId);
        if (account) {
          return `${account.name} (${account.upi?.upiId || "UPI"})`;
        }

        // Handle enum payment methods
        switch (methodId) {
          case PaymentMethodEnum.Cash:
            return "Cash";
          case PaymentMethodEnum.Card:
            return "Card";
          case PaymentMethodEnum.Other:
            return "Other";
          default:
            return "";
        }
      });

      return methodNames.join(", ");
    } else {
      // For single selection
      if (selectedMethod === PaymentMethodEnum.Cash) return "Cash";
      if (selectedMethod === PaymentMethodEnum.Card) return "Card";
      if (selectedMethod === PaymentMethodEnum.Other) return "Other";

      // For UPI accounts
      const selectedAccount = accounts.find(
        (acc) => acc._id === selectedMethod
      );
      return selectedAccount
        ? selectedAccount.upi?.upiId || "UPI"
        : "";
    }
  };

  const isMethodSelected = (methodId) => {
    if (!isSplitActive) {
      return selectedMethod === methodId;
    }
    return (watch("paymentMethods") || []).includes(methodId);
  };

  return (
    <div
      className={`paymentMethodContainer ${isExpanded ? "expanded" : "collapsed"}`}
    >
      <div className="paymentMethodHeader" onClick={toggleExpanded}>
        <h3 className="containerHeading">Payment Method</h3>
        <div className="paymentMethodSummary">
          {getDisplayMethod()}
        </div>
      </div>
      {isExpanded && (
        <>
          <div className="splitContainer">
            <button
              className={`splitToggle ${!isSplitActive ? "active" : ""}`}
              onClick={deactivateSplitPayment}
              type="button"
            >
              Payment
            </button>
            <button
              type="button"
              className={`splitToggle ${isSplitActive ? "active" : ""}`}
              onClick={activateSplitPayment}
            >
              <span>Split</span>
            </button>
          </div>
          <h4 className="paymentSubheading">UPI</h4>
          <div className="paymentMethodCard">
            {accounts?.map((bank) => (
              <div
                key={bank._id}
                className={`bankOption ${isMethodSelected(bank._id) ? "selected" : ""}`}
              >
                <label className="bankLabel">
                  <div className="bankInfo">
                    <img
                      src={bank.logoURL}
                      alt={bank.name}
                      className="bankLogo"
                    />
                    <div className="bankText">
                      <span className="bankName">{bank.name}</span>
                      <span className="bankDetails">{bank.upi?.upiId}</span>
                    </div>
                  </div>
                  <div className="paymentRightSection">
                    {isSplitActive && (
                      <div className="splitAmountInput">
                        <span className="currencySymbol">₹</span>
                        <input
                          type="number"
                          className="amountInput"
                          {...register(`splitAmounts.${bank._id}`)}
                          placeholder="0.00"
                          step={0.1}
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                      </div>
                    )}
                    {!isSplitActive && (
                      <input
                        type="radio"
                        checked={isMethodSelected(bank._id)}
                        onChange={() => handleMethodSelection(bank._id)}
                        className="bankRadioInput"
                      />
                    )}
                  </div>
                </label>
              </div>
            ))}
          </div>

          <div
            className={`paymentOption ${isMethodSelected(PaymentMethodEnum.Cash) ? "selected" : ""}`}
          >
            <label className="optionLabel">
              <div className="optionInfo">
                <div className="optionText">
                  <span className="optionName">Cash</span>
                </div>
              </div>
              <div className="paymentRightSection">
                {isSplitActive && (
                  <div className="splitAmountInput">
                    <span className="currencySymbol">₹</span>
                    <input
                      type="number"
                      className="amountInput"
                      {...register(`splitAmounts.${PaymentMethodEnum.Cash}`)}
                      placeholder="0.00"
                      onWheel={(e) => e.currentTarget.blur()}
                    />
                  </div>
                )}
                {!isSplitActive && (
                  <input
                    type="radio"
                    checked={isMethodSelected(PaymentMethodEnum.Cash)}
                    onChange={() => handleMethodSelection(PaymentMethodEnum.Cash)}
                    className="optionRadioInput"
                  />
                )}
              </div>
            </label>
          </div>
          <div className='breakLine' />
          <div
            className={`paymentOption ${isMethodSelected(PaymentMethodEnum.Card) ? "selected" : ""}`}
          >
            <label className="optionLabel">
              <div className="optionInfo">
                <div className="optionText">
                  <span className="optionName">Debit / Credit / ATM Card</span>
                </div>
              </div>
              <div className="paymentRightSection">
                {isSplitActive && (
                  <div className="splitAmountInput">
                    <span className="currencySymbol">₹</span>
                    <input
                      type="number"
                      className="amountInput"
                      {...register(`splitAmounts.${PaymentMethodEnum.Card}`)}
                      placeholder="0.00"
                      onWheel={(e) => e.currentTarget.blur()}
                    />
                  </div>
                )}
                {!isSplitActive && (
                  <input
                    type="radio"
                    checked={isMethodSelected(PaymentMethodEnum.Card)}
                    onChange={() => handleMethodSelection(PaymentMethodEnum.Card)}
                    className="optionRadioInput"
                  />
                )}
              </div>
            </label>
          </div>
          <div className='breakLine' />
          <div
            className={`paymentOption ${isMethodSelected(PaymentMethodEnum.Other) ? "selected" : ""}`}
          >
            <label className="optionLabel">
              <div className="optionInfo">
                <div className="optionText">
                  <span className="optionName">Other</span>
                </div>
              </div>
              <div className="paymentRightSection">
                {isSplitActive && (
                  <div className="splitAmountInput">
                    <span className="currencySymbol">₹</span>
                    <input
                      type="number"
                      className="amountInput"
                      {...register(`splitAmounts.${PaymentMethodEnum.Other}`)}
                      placeholder="0.00"
                      onWheel={(e) => e.currentTarget.blur()}
                    />
                  </div>
                )}
                {!isSplitActive && (
                  <input
                    type="radio"
                    checked={isMethodSelected(PaymentMethodEnum.Other)}
                    onChange={() => handleMethodSelection(PaymentMethodEnum.Other)}
                    className="optionRadioInput"
                  />
                )}
              </div>
            </label>
          </div>
        </>
      )}
    </div>
  );
};


function PaymentSummary({ isExpanded, toggleExpanded }) {
  const { watch, setValue, control } = useFormContext();
  const isApplied = watch("isApplied") || false;
  const [isToggle, setIsToggle] = useState(false);
  const currentUser = useSelector(currentUserSelector);
  const [showPurchasePriceTotal, setShowPurchasePriceTotal] = useState(false);
  const canViewPurchasePrice = currentUser?.profileDetail?.saleOrderConfig?.showPurchasePrice ?? true;
  const [isEditing, setIsEditing] = useState(false);
  const [totalAmountStr, setTotalAmountStr] = useState<string>("");
  const [isSummaryVisible, setIsSummaryVisible] = useState(true);

  const items = watch("productList") || [];
  const discount = watch("discount") || {
    type: DiscountType.Percent,
    value: 0,
  };

  const grossPrice = DecimalCalc.sum(
    items.map((item: SellerProductDetail & { quantity: number }) =>
      decimal(Math.max(item?.price?.mrp, item.price.sellingPrice)).mul(item.quantity)
    )
  ).toNumber();

  const itemTotal = DecimalCalc.sum(
    items.map((item: SellerProductDetail & { quantity: number }) =>
      decimal(item?.price?.sellingPrice).mul(item.quantity)
    )
  ).toNumber();

  const productDiscount = DecimalCalc.sum(
    items.map((item: SellerProductDetail & { quantity: number }) =>
      decimal(
        Math.max(0, item.price.mrp - Number(item?.price?.sellingPrice))
      ).mul(item.quantity)
    )
  ).toNumber();

  const additionalDiscount = isApplied
    ? discount.type === DiscountType.Amount
      ? discount.value
      : DecimalCalc.round(decimal(itemTotal).mul(discount.value).div(100)).toNumber()
    : 0;

  const computedTotalAmountToReceive = Math.max(
    DecimalCalc.round(decimal(itemTotal).sub(additionalDiscount)).toNumber(),
    0
  );

  const purchaseTotal = DecimalCalc.sum(
    items.map((item: SellerProductDetail & { quantity: number }) =>
      decimal(item?.price?.purchasePrice || 0).mul(item?.quantity || 0)
    )
  ).toNumber();

  useEffect(() => {
    if (!isEditing) {
      setTotalAmountStr(computedTotalAmountToReceive.toString());
    }
  }, [computedTotalAmountToReceive, isEditing]);

  useEffect(() => {
    if (!isEditing) {
      setTotalAmountStr(computedTotalAmountToReceive.toString());
    }
  }, [items.length]);

  const handleQuickDiscount = (percentage: number) => {
    setValue("discount", { type: DiscountType.Percent, value: percentage });
    setValue("isApplied", true);
    setIsToggle(false);
  };

  const quickDiscounts = [10, 30, 50];

  const handleFocus = () => {
    setIsEditing(true);
    setTotalAmountStr(computedTotalAmountToReceive.toString());
  };

  const handleChange = (raw: string) => {
    setTotalAmountStr(raw);
  };

  const handleBlur = () => {
    const parsed = totalAmountStr === "" ? 0 : parseFloat(totalAmountStr);
    const safeParsed = Number.isFinite(parsed) ? parsed : 0;
    const maxAmount = itemTotal;
    const newAmount = DecimalCalc.round(Math.max(0, Math.min(safeParsed, maxAmount))).toNumber();
    if (decimal(newAmount).lessThan(maxAmount)) {
      setValue("isApplied", true);
      if (discount.type === DiscountType.Percent) {
        const discountPercent = decimal(maxAmount).sub(newAmount).div(maxAmount).mul(100).toNumber();
        
        setValue("discount.value", Math.min(discountPercent, 100));
      } else {
        const discountAmount = DecimalCalc.round(decimal(maxAmount).sub(newAmount)).toNumber();
        setValue("discount.value", discountAmount);
      }
    } else {
      setValue("isApplied", false);
      setValue("discount.value", 0);
    }

    setIsEditing(false);
    setTotalAmountStr(newAmount.toString());
  };

  function handleDiscountChange(
    raw: string,
    discountType: DiscountType,
    onChange: (val: number | "") => void
  ) {
    if (raw === "") {
      onChange("");
      return;
    }

    let val = Number(raw);

    if (val < 0) {
      val = 0;
    }

    if (discountType === DiscountType.Percent) {
      onChange(DecimalCalc.round(val > 100 ? 100 : val).toNumber());
    } else {
      onChange(DecimalCalc.round(val).toNumber());
    }
  }
  const displayTotalAmount = computedTotalAmountToReceive.toFixed(2);

  return (
    <div
      className={`paymentSummaryCard ${isExpanded ? "expanded" : "collapsed"}`}
    >
      <div className="paymentSummaryHeader" onClick={toggleExpanded}>
        <h3 className="containerHeading">Payment Summary</h3>
        <div className="paymentSummaryTotal">
          {currencyFormatter(computedTotalAmountToReceive)}
        </div>
      </div>

      {isExpanded && (
        <>
          <h3 className="f500 pLeft4 pbottom4 ptop4" >Discount</h3>
          <div className="discountContainer">

            <Controller
              control={control}
              name="discount.value"
              render={({ field }) => (
                <input
                  type="number"
                  className="discountInput"
                  step='any'
                  min={0}
                  max={discount.type === DiscountType.Percent ? 100 : undefined}
                  value={field.value != null && field.value !== "" ? parseFloat(Number(field.value).toFixed(2)) : ""}
                  onWheel={(e) => e.currentTarget.blur()}
                  onChange={(e) =>
                    handleDiscountChange(
                      e.target.value,
                      discount.type,
                      field.onChange
                    )
                  }
                />
              )}
            />

            <div className="togglePercentageContainer">
              <button
                type="button"
                onClick={() => {
                  if (discount.type === DiscountType.Amount) {
                    const percentValue = itemTotal > 0
                      ? decimal(discount.value).div(itemTotal).mul(100).toNumber()
                      : 0;
                    setValue("discount", {
                      type: DiscountType.Percent,
                      value: Math.min(percentValue, 100),
                    });
                  }
                  setIsToggle(false);
                }}
                className={`togglePercentageBtn ${!isToggle ? "Switched" : ""}`}
              >
                <Percent size={20} />
              </button>

              <button
                type="button"
                onClick={() => {
                  if (discount.type === DiscountType.Percent) {
                    const rupeeValue = DecimalCalc.round(
                      decimal(itemTotal).mul(discount.value).div(100)
                    ).toNumber();
                    setValue("discount", {
                      type: DiscountType.Amount,
                      value: rupeeValue,
                    });
                  }
                  setIsToggle(true);
                }}
                className={`togglePercentageBtn ${isToggle ? "Switched" : ""}`}
              >
                <IndianRupee size={20} />
              </button>
            </div>
            <button
              className={`applyDiscountButton ${isApplied ? "discountApplied" : ""}`}
              onClick={() => {
                const newApplied = !isApplied;
                setValue("isApplied", newApplied);

                if (!newApplied) {
                  setValue("discount", { type: discount.type, value: 0 });
                } else {
                  setValue("discount", {
                    type: discount.type,
                    value: discount.value,
                  });
                }
              }}
              type="button"
            >
              {isApplied ? "Applied" : "Apply"}
            </button>
          </div>

          <div className="quickDiscounts">
            {quickDiscounts.map((percentage) => (
              <button
                key={percentage}
                className={`quickDiscountButton ${discount.type === DiscountType.Percent &&
                  discount.value === percentage
                  ? "quickDiscountSelected"
                  : ""
                  }`}
                onClick={() => handleQuickDiscount(percentage)}
                type="button"
              >
                {percentage}%
              </button>
            ))}
          </div>

          <div className="breakLine2" />

          <div className="paymentSummary">
            <div className="summaryHeader">
              <span>Total Bill</span>
              <button
                className={`summaryToggleButton ${!isSummaryVisible ? "summaryToggleRotated" : ""}`}
                onClick={() => setIsSummaryVisible(!isSummaryVisible)}
                type="button"
              >
                <span>Details</span>
                <img
                  src={toggleIcon}
                  alt="Toggle details"
                  className={`toggleIcon ${!isSummaryVisible ? "toggleIconRotated" : ""}`}
                />
              </button>
            </div>

            {isSummaryVisible && (
              <div className="summaryDetails">
                <div className="summaryRow">
                  <span>Gross Price</span>
                  <span>
                    {currencyFormatter(Math.max(grossPrice, itemTotal))}
                  </span>
                </div>

                <div className="summaryRow">
                  <span>Product Discount</span>
                  <span>-{currencyFormatter(productDiscount)}</span>
                </div>
                <div className="summaryRow">
                  <span>Item Total</span>
                  <span>{currencyFormatter(itemTotal)}</span>
                </div>
                <div className="summaryRow">
                  <span>Additional Discount</span>
                  <span>-{currencyFormatter(additionalDiscount)}</span>
                </div>
              </div>
            )}

            <div className="summaryTotal">
              <div className="totalPurchasePrice">
                <span>Total Amount to receive</span>
                {canViewPurchasePrice && (
                  <button
                    className="purchasePrice"
                    onClick={() => setShowPurchasePriceTotal(!showPurchasePriceTotal)}
                    type="button"
                  >
                    {showPurchasePriceTotal ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 20 20" fill="none">
                        <path d="M9.99967 7.5C10.6627 7.5 11.2986 7.76339 11.7674 8.23223C12.2363 8.70107 12.4997 9.33696 12.4997 10C12.4997 10.663 12.2363 11.2989 11.7674 11.7678C11.2986 12.2366 10.6627 12.5 9.99967 12.5C9.33663 12.5 8.70075 12.2366 8.23191 11.7678C7.76307 11.2989 7.49967 10.663 7.49967 10C7.49967 9.33696 7.76307 8.70107 8.23191 8.23223C8.70075 7.76339 9.33663 7.5 9.99967 7.5ZM9.99967 3.75C14.1663 3.75 17.7247 6.34167 19.1663 10C17.7247 13.6583 14.1663 16.25 9.99967 16.25C5.83301 16.25 2.27467 13.6583 0.833008 10C2.27467 6.34167 5.83301 3.75 9.99967 3.75ZM2.64967 10C3.32322 11.3753 4.3691 12.534 5.66841 13.3444C6.96772 14.1548 8.46834 14.5844 9.99967 14.5844C11.531 14.5844 13.0316 14.1548 14.3309 13.3444C15.6303 12.534 16.6761 11.3753 17.3497 10C16.6761 8.62474 15.6303 7.46604 14.3309 6.65562C13.0316 5.8452 11.531 5.41557 9.99967 5.41557C8.46834 5.41557 6.96772 5.8452 5.66841 6.65562C4.3691 7.46604 3.32322 8.62474 2.64967 10Z" fill={appColors.accentBlue} />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 20 20" fill="none">
                        <path d="M1.66634 4.39159L2.73301 3.33325L16.6663 17.2666L15.608 18.3333L13.0413 15.7666C12.083 16.0833 11.0663 16.2499 9.99967 16.2499C5.83301 16.2499 2.27467 13.6583 0.833008 9.99992C1.40801 8.53325 2.32467 7.24159 3.49134 6.21659L1.66634 4.39159ZM9.99967 7.49992C10.6627 7.49992 11.2986 7.76331 11.7674 8.23215C12.2363 8.70099 12.4997 9.33688 12.4997 9.99992C12.5001 10.2837 12.4522 10.5655 12.358 10.8333L9.16634 7.64159C9.43406 7.5474 9.71587 7.4995 9.99967 7.49992ZM9.99967 3.74992C14.1663 3.74992 17.7247 6.34159 19.1663 9.99992C18.4858 11.7274 17.3301 13.2268 15.833 14.3249L14.6497 13.1333C15.8021 12.3361 16.7316 11.2575 17.3497 9.99992C16.6761 8.62481 15.6301 7.46628 14.3308 6.65605C13.0315 5.84581 11.5309 5.41639 9.99967 5.41659C9.09134 5.41659 8.19968 5.56659 7.36634 5.83325L6.08301 4.55825C7.28301 4.04159 8.60801 3.74992 9.99967 3.74992ZM2.64967 9.99992C3.3233 11.375 4.36922 12.5336 5.66854 13.3438C6.96785 14.154 8.46843 14.5834 9.99967 14.5833C10.5747 14.5833 11.1413 14.5249 11.6663 14.4083L9.76634 12.4999C9.18648 12.4378 8.64536 12.179 8.23299 11.7666C7.82061 11.3542 7.56183 10.8131 7.49967 10.2333L4.66634 7.39158C3.84134 8.09992 3.14967 8.98325 2.64967 9.99992Z" fill={appColors.accentBlue} />
                      </svg>
                    )}
                    {showPurchasePriceTotal ? (
                      <p>{currencyFormatter(purchaseTotal)}</p>
                    ) : (
                      <p>Purchase Price Total</p>
                    )}
                  </button>
                )}
              </div>

              <input
                type="number"
                className="totalAmountInput"
                value={
                  isEditing
                    ? totalAmountStr
                    : computedTotalAmountToReceive
                }
                onFocus={handleFocus}
                onChange={(e) => handleChange(e.target.value)}
                onBlur={handleBlur}
                onWheel={(e) => e.currentTarget.blur()}
                step="0.01"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}


